import {
  type ContactsListData,
  type PaginatedReturnContactListReadable,
  type ReturnContactReadable,
  contactsListInfiniteOptions,
  contactsListQueryKey,
  contactsPartialUpdateMutation,
  leadsContactsListOptions,
  leadsContactsListQueryKey,
  leadsLogsListQueryKey,
  opportunitiesContactsListOptions,
  opportunitiesContactsListQueryKey,
  opportunitiesLogsListQueryKey
} from '@crm/api'
import { DEFAULT_PER_PAGE } from '@crm/app-constants'
import { DuplicateAlert, InfiniteSelect, Text } from '@crm/components'
import { ContactForm } from '@crm/features/contacts/components'
import { handleContactMutationError } from '@crm/utils'
import { useInvalidatePredicateQueries } from '@shared/hooks'
import { transformOptions } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button, Divider, Flex, Form, Modal, theme } from 'antd'
import { Add, Mobile, Profile, Sms } from 'iconsax-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useParams } from 'react-router-dom'

import { useStyles } from './styles'

import type { ModalProps } from 'antd/lib'

type Props = {
  onCancel?: (closeParentModal?: boolean) => void
  onSubmit?: (id: number) => unknown | Promise<unknown>
  showFooter?: boolean
} & Omit<ModalProps, 'onCancel'>

export const AddExistingContactModalForm = ({
  onCancel,
  onSubmit: onSubmitCallback,
  showFooter = true,
  ...props
}: Props) => {
  const [form] = Form.useForm()
  const [formAddNewContact] = Form.useForm()

  const [isModalAddContactOpen, setIsModalAddContactOpen] = useState(false)
  const [selectedContactId, setSelectedContactId] = useState<number>()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { id: leadId, opportunityId } = useParams()
  const { message } = App.useApp()
  const { t } = useTranslation()
  const { token } = theme.useToken()
  const { styles } = useStyles()

  const { data: leadContacts } = useQuery({
    ...leadsContactsListOptions({
      path: { lead_id: String(leadId) }
    }),
    enabled: !!props.open && !!leadId && !opportunityId
  })

  const { data: opportunityContacts } = useQuery({
    ...opportunitiesContactsListOptions({
      path: { opportunity_id: String(opportunityId) }
    }),
    enabled: !!props.open && !!opportunityId
  })

  const { mutateAsync, isPending, error, reset } = useMutation({
    ...contactsPartialUpdateMutation(),
    onSuccess: () => {
      invalidateQueries([
        leadsContactsListQueryKey({
          path: { lead_id: String(leadId) }
        }),
        opportunitiesContactsListQueryKey({
          path: { opportunity_id: String(opportunityId) }
        }),
        leadsLogsListQueryKey({ path: { lead_id: String(leadId) } }),
        opportunitiesLogsListQueryKey({
          path: { opportunity_id: String(opportunityId) }
        }),
        contactsListQueryKey()
      ])
      onCloseModal(true)
      message.success(t('success.success'))
    },
    onError: (e) => handleContactMutationError(e, form)
  })

  const onSubmit = async () => {
    if (leadId || opportunityId) {
      await form.validateFields()

      await mutateAsync({
        path: { id: Number(selectedContactId) },
        body: {
          ...(leadId ? { leads: [Number(leadId)] } : {}),
          ...(opportunityId ? { opportunities: [Number(opportunityId)] } : {})
        }
      })
    }

    await onSubmitCallback?.(Number(selectedContactId))
  }

  const onSelect = (id: number) => setSelectedContactId(id)
  const onClear = () => setSelectedContactId(undefined)
  const onCloseModal = (closeParentModal?: boolean) => {
    onCancel?.(!!closeParentModal)
    form.resetFields()
    setSelectedContactId(undefined)
  }
  const onOpenModalAddContact = () => setIsModalAddContactOpen(true)

  const onFilterOption = (input: string, option: ReturnContactReadable) => {
    const query = input.toLowerCase()
    const first = option.first_name?.toLowerCase() ?? ''
    const last = option.last_name?.toLowerCase() ?? ''

    return first.includes(query) || last.includes(query)
  }

  const renderTextWithFallback = (
    text?: string | number | null,
    isBold?: boolean
  ) => {
    const content =
      text !== null && text !== undefined && text !== '' ? text : '-'

    return isBold ? (
      <span className={styles.textBold}>{content}</span>
    ) : (
      <span className={styles.colorSecondary}>{content}</span>
    )
  }

  const popupRender = () => (
    <>
      <Divider className={styles.dividerMargin} />

      <Flex justify="space-between" align="center" className={styles.footer}>
        <Text>{t('contacts.do_not_found_existing')}</Text>

        <Divider type="vertical" />

        <Button
          type="primary"
          onClick={onOpenModalAddContact}
          icon={<Add size={16} />}
        >
          {t('buttons.add')}
        </Button>
      </Flex>
    </>
  )

  const iconSize = 16
  const optionRender = (contact: ReturnContactReadable) => (
    <Flex gap={2} vertical>
      <Flex align="center" gap={6}>
        <Profile
          size={iconSize}
          color={token.colorTextHeading}
          className={styles.marginLeftIcon}
        />
        {renderTextWithFallback(contact?.first_name, true)}
        <span className={styles.textBold}>
          {contact?.last_name ? ` ${contact?.last_name}` : 'Unnamed'}
        </span>
      </Flex>

      <Flex align="center" gap="middle">
        <Flex align="center" gap={4}>
          <Mobile size={iconSize} color={token.colorTextDescription} />
          {renderTextWithFallback(
            contact?.phone_numbers?.filter(({ is_primary }) => is_primary)?.[0]
              ?.number
          )}
        </Flex>

        <Flex align="center" gap={4}>
          <Sms size={iconSize} color={token.colorTextDescription} />{' '}
          {renderTextWithFallback(contact?.email)}
        </Flex>
      </Flex>
    </Flex>
  )

  const contactsSet = new Set(
    (opportunityContacts?.results.length ?? 0) > 0
      ? opportunityContacts?.results.map(({ id }) => id)
      : leadContacts?.results.map(({ id }) => id)
  )

  return (
    <>
      <Modal
        title={t('contacts.select_contact')}
        width={450}
        maskClosable={false}
        {...props}
        onCancel={() => onCloseModal(false)}
        okButtonProps={{
          onClick: onSubmit,
          loading: isPending,
          disabled: !selectedContactId
        }}
      >
        <Form form={form} layout="vertical">
          <DuplicateAlert error={error} onClose={reset} />

          <Form.Item
            name="contact_id"
            label={t('contacts.contact')}
            rules={[{ required: true }]}
            required
          >
            <InfiniteSelect<
              ContactsListData,
              PaginatedReturnContactListReadable,
              ReturnContactReadable
            >
              getQueryFunction={contactsListInfiniteOptions}
              baseOptions={{
                query: {
                  per_page: DEFAULT_PER_PAGE + contactsSet.size,
                  preference: selectedContactId
                    ? [selectedContactId]
                    : undefined
                }
              }}
              onSelect={(id: number) => onSelect(id)}
              onClear={onClear}
              getOptions={({ options }) =>
                transformOptions({
                  options: options.filter(({ id }) => !contactsSet.has(id)),
                  label: (option) =>
                    `${option?.first_name} ${option?.last_name || 'Unamed'}`,
                  value: 'id',
                  returnOption: true
                })
              }
              popupRender={(menu) => (
                <>
                  {menu}
                  {showFooter && popupRender()}
                </>
              )}
              optionRender={({ data: contact }) =>
                optionRender(contact as ReturnContactReadable)
              }
              filterOption={(input, option) =>
                onFilterOption(input, option as ReturnContactReadable)
              }
              placeholder={t('main.select')}
            />
          </Form.Item>
        </Form>
      </Modal>

      <ContactForm
        form={formAddNewContact}
        open={isModalAddContactOpen}
        onClose={() => onCloseModal(true)}
        initialValues={{
          ...(leadId ? { leads: [Number(leadId)] } : {}),
          ...(opportunityId ? { opportunities: [Number(opportunityId)] } : {})
        }}
      />
    </>
  )
}
